/**
 * ArtefactsPanel Component
 *
 * Main panel component with tabbed interface for Citations and Generated Content.
 * Integrates with ChatStore artefacts state and provides smooth HeroUI experience.
 */

import React, { useRef, useEffect, useState, useCallback } from "react";
import { Button, toast, Tooltip, TooltipTrigger, TooltipContent } from "@heroui/react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useTranslation } from "react-i18next";
import { useUser, useAuth } from "@webuild-ai/react-auth";
import { useBrand } from "@webuild-ai/react-ui/hooks";

import { useArtefactStore } from "@/stores/ArtefactStore";
import { batchExport, BatchExportFormat } from "@/utils/artefactExport";
import { Artefact, ArtefactType } from "@/types/artefacts";
import { useTheme } from "@/hooks/use-theme";
import { logger } from "@/telemetry";

import { ArtefactExportDialog } from "./ArtefactExportDialog";
import { CitationsList } from "./CitationsList";
import { ArtefactsList } from "./ArtefactsList";

export const ArtefactsPanel: React.FC = () => {
  const { getFilteredArtefactsForCurrentChat, getCitationsForCurrentChat, toggleArtefacts } =
    useArtefactStore();
  const { t } = useTranslation();
  const { brand } = useBrand();
  const { userProfile } = useUser();
  const { user } = useAuth();
  const { theme } = useTheme();
  const getAuthorName = useCallback(
    () => userProfile?.name || user?.name || brand.name,
    [userProfile?.name, user?.name, brand.name],
  );
  const tabsRef = useRef<HTMLDivElement>(null);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);

  const artefacts = getFilteredArtefactsForCurrentChat();
  const hasArtefacts = artefacts.length > 0;
  /*
    ⚠️ This control must stay reachable for a single artefact, and the label is
    the only thing that varies with the count.

    It was briefly hidden below two artefacts, on the premise that it and the
    card's download "take the same bytes from the same artefact". That premise is
    wrong. `ArtefactsPanel` is the only mount point for `ArtefactExportDialog` in
    the codebase, and the dialog offers a custom document title, a per-export
    template override, an export font, PDF and Word previews, PowerPoint, and the
    Excel separate-worksheets option, none of which the card can do. The card runs
    an entirely separate pipeline in `handleDownloadWithFormat`. Worse, the card's
    action lives inside `ArtefactHeader`, which renders only once the artefact is
    expanded, so a single-artefact chat with a collapsed card was left with no
    export affordance anywhere and no way to produce a branded Word document.
  */
  const hasMultipleArtefacts = artefacts.length > 1;

  const citations = getCitationsForCurrentChat();
  const hasCitations = citations.length > 0;

  // Hooks must be called before any conditional returns (Rules of Hooks)
  const [selectedTab, setSelectedTab] = useState<string>(hasCitations ? "citations" : "artefacts");

  // Update selected tab when content availability changes
  useEffect(() => {
    if (!hasCitations && hasArtefacts && selectedTab === "citations") {
      setSelectedTab("artefacts");
    } else if (!hasArtefacts && hasCitations && selectedTab === "artefacts") {
      setSelectedTab("citations");
    }
  }, [hasArtefacts, hasCitations, selectedTab]);

  // Handle batch export
  const handleBatchExport = useCallback(
    async (
      orderedArtefacts: Artefact[],
      format: BatchExportFormat,
      title?: string,
      useSeparateWorksheets?: boolean,
      fontFamily?: string,
      codeFontFamily?: string,
    ) => {
      // Count HTML artefacts for better progress messaging
      const htmlCount = orderedArtefacts.filter((a) => a.type === ArtefactType.HTML).length;

      // Show specific progress for HTML capture if present
      if (htmlCount > 0) {
        toast(t("Capturing {{count}} charts...", { count: htmlCount }), {
          description: t("Please wait while we prepare your images"),
        });
      } else {
        const formatKey =
          format === "powerpoint"
            ? t("Exporting to PowerPoint...")
            : format === "word"
              ? t("Exporting to Word...")
              : format === "pdf"
                ? t("Exporting to PDF...")
                : t("Exporting to Excel...");

        toast(formatKey, { description: t("Please wait while we prepare your export") });
      }

      try {
        await batchExport(orderedArtefacts, {
          format,
          title: title || undefined,
          author: getAuthorName(),
          useSeparateWorksheets,
          itemsLabel: t("{{count}} items", { count: orderedArtefacts.length }),
          brand,
          brandName: brand.name,
          theme,
          fontFamily,
          codeFontFamily,
        });

        toast.success(t("Export successful!"), {
          description: t("Your content has been exported"),
        });
      } catch (error) {
        logger.error("Batch export failed", error);
        toast.danger(t("Export failed"), {
          description: t("Unable to export content. Please try again."),
        });
      }
    },
    [t, brand, theme, getAuthorName],
  );

  // Don't render the panel at all if there's nothing to show
  if (!hasArtefacts && !hasCitations) {
    return null;
  }

  // Focus first interactive element when panel opens
  useEffect(() => {
    // Small delay to account for animation
    const focusTimeout = setTimeout(() => {
      const firstButton = tabsRef.current?.querySelector('button[role="tab"]') as HTMLElement;

      firstButton?.focus();
    }, 100);

    return () => clearTimeout(focusTimeout);
  }, []); // Run once on mount

  // Add keyboard handlers for ] (toggle panel) and Cmd/Ctrl+E (export)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip if user is typing in an input field
      const activeElement = document.activeElement;
      const isTyping =
        activeElement?.tagName === "INPUT" ||
        activeElement?.tagName === "TEXTAREA" ||
        (activeElement as HTMLElement)?.isContentEditable;

      if (isTyping) return;

      // ] key - toggle panel closed
      if (e.key === "]") {
        toggleArtefacts();
        // Return focus to trigger button (use data-testid which is language-independent)
        const triggerButton = document.querySelector(
          '[data-testid="artefacts-toggle"]',
        ) as HTMLElement;

        triggerButton?.focus();
      }

      // Cmd/Ctrl+E - open export dialog (only if artefacts exist and dialog is not already open)
      // Check both lowercase and uppercase 'e'/'E'
      if ((e.metaKey || e.ctrlKey) && (e.key === "e" || e.key === "E")) {
        logger.info("🔍 Ctrl/Cmd+E detected, checking conditions", {
          hasArtefacts: String(hasArtefacts),
          isExportDialogOpen: String(isExportDialogOpen),
          shouldOpen: String(hasArtefacts && !isExportDialogOpen),
        });

        if (hasArtefacts && !isExportDialogOpen) {
          e.preventDefault();
          e.stopPropagation();
          logger.info("✅ Opening export dialog via keyboard shortcut");
          setIsExportDialogOpen(true);
        }
      }
    };

    // Use capture phase to intercept events before they reach child elements
    document.addEventListener("keydown", handleKeyDown, true);

    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [toggleArtefacts, hasArtefacts, isExportDialogOpen]);

  return (
    <div
      aria-label={t("Artefacts and citations panel")}
      className="w-full h-full bg-background flex flex-col"
      role="complementary"
    >
      {/* Tab header */}
      <div
        ref={tabsRef}
        className="flex items-center justify-between border-b border-divider bg-background px-3 py-2"
      >
        <div aria-label="Content tabs" className="flex flex-1" role="tablist">
          {hasCitations && (
            <button
              aria-selected={selectedTab === "citations"}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors rounded-full ${
                selectedTab === "citations"
                  ? "bg-brand-primary text-white"
                  : "text-default-500 hover:bg-default-100"
              }`}
              role="tab"
              onClick={() => setSelectedTab("citations")}
            >
              <Icon icon="solar:document-text-outline" width={16} />
              <span>{t("Citations")}</span>
              {citations.length > 0 && (
                <span
                  className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                    selectedTab === "citations"
                      ? "bg-white/20 text-white"
                      : "bg-default-100 text-default-500"
                  }`}
                >
                  {citations.length}
                </span>
              )}
            </button>
          )}
          {hasArtefacts && (
            <button
              aria-selected={selectedTab === "artefacts"}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors rounded-full ${
                selectedTab === "artefacts"
                  ? "bg-brand-primary text-white"
                  : "text-default-500 hover:bg-default-100"
              }`}
              role="tab"
              onClick={() => setSelectedTab("artefacts")}
            >
              <Icon
                className={selectedTab === "artefacts" ? "text-white" : "text-default-500"}
                icon="solar:code-bold"
                width={16}
              />
              <span>{t("Generated Content")}</span>
              {artefacts.length > 0 && (
                <span
                  className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                    selectedTab === "artefacts"
                      ? "bg-white/20 text-white"
                      : "bg-default-100 text-default-500"
                  }`}
                >
                  {artefacts.length}
                </span>
              )}
            </button>
          )}
        </div>

        {/* Export, shown whenever there is anything to export. Styled with
            `btn-brand-primary` to match the copy, refresh and download actions on
            the artefact card. As a `ghost` variant it was the only pale outlined
            control among solid brand-coloured ones, so it read as a different kind
            of thing rather than as another action. */}
        {hasArtefacts && (
          <Tooltip>
            <TooltipTrigger>
              <Button
                isIconOnly
                aria-label={
                  hasMultipleArtefacts
                    ? t("Export all content (Ctrl/Cmd+E)")
                    : t("Export content (Ctrl/Cmd+E)")
                }
                className="btn-brand-primary ml-2"
                size="sm"
                onPress={() => setIsExportDialogOpen(true)}
              >
                <Icon height={16} icon="solar:export-bold" width={16} />
              </Button>
            </TooltipTrigger>
            {/* Neither wording is "Download", because the artefact cards beneath
                carry their own download action and two controls both labelled
                "Download" gave the reader no way to tell them apart. For one
                artefact the distinction is not quantity but capability, since this
                route is the only one offering a format, a template, a title and a
                preview. */}
            <TooltipContent>
              {hasMultipleArtefacts ? t("Batch download") : t("Export with options")}
            </TooltipContent>
          </Tooltip>
        )}
      </div>

      {/* Tab panels */}
      <div className="flex-1 overflow-hidden">
        {selectedTab === "citations" && hasCitations && <CitationsList />}
        {selectedTab === "artefacts" && hasArtefacts && <ArtefactsList />}
      </div>

      {/* Export Dialog */}
      <ArtefactExportDialog
        artefacts={artefacts}
        brand={brand}
        isOpen={isExportDialogOpen}
        theme={theme}
        onExport={handleBatchExport}
        onOpenChange={(open: boolean) => {
          if (!open) setIsExportDialogOpen(false);
        }}
      />
    </div>
  );
};
